import {
  BgColorsOutlined,
  CompressOutlined,
  DesktopOutlined,
  ExpandOutlined,
  FormatPainterOutlined,
  LayoutOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Button, Card, Col, Divider, Radio, Row, Segmented, Space, Switch, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import {
  applyUiPreferences,
  defaultUiPreferences,
  getUiPreferences,
  saveUiPreferences,
} from '../../Utils/uiPreferences';

const { Title, Text } = Typography;

const colorPresets = [
  { name: 'Bolde Blue', value: '#003D79' },
  { name: 'Ocean', value: '#006D75' },
  { name: 'Emerald', value: '#237804' },
  { name: 'Violet', value: '#531DAB' },
  { name: 'Ruby', value: '#A8071A' },
  { name: 'Orange', value: '#AD4E00' },
];

function SettingItem({ icon, title, description, children }) {
  return (
    <div className="settings-item">
      <div className="settings-item__info">
        <div className="settings-item__icon">{icon}</div>
        <div>
          <Text strong>{title}</Text>
          <div><Text type="secondary">{description}</Text></div>
        </div>
      </div>
      <div className="settings-item__control">{children}</div>
    </div>
  );
}

function Settings() {
  const [preferences, setPreferences] = useState(getUiPreferences());

  useEffect(() => {
    applyUiPreferences(preferences);
  }, [preferences]);

  const updatePreference = (key, value) => {
    const next = { ...preferences, [key]: value };
    setPreferences(next);
    saveUiPreferences(next);
  };


  const resetPreferences = () => {
    setPreferences(defaultUiPreferences);
    saveUiPreferences(defaultUiPreferences);
    message.success('Pengaturan tampilan dikembalikan ke default.');
  };

  return (
    <div className="settings-page responsive-page">
      <div className="settings-page__hero">
        <div>
          <Text className="settings-page__eyebrow">PERSONAL PREFERENCES</Text>
          <Title level={2} className="settings-page__title">Settings</Title>
          <Text type="secondary">Atur tampilan Bolde Access sesuai cara kerja Anda.</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={resetPreferences}>Reset</Button>
      </div>

      <Row gutter={[18, 18]}>
        <Col xs={24} xl={15}>
          <Card className="settings-card" bordered={false}>
            <div className="settings-card__heading">
              <FormatPainterOutlined />
              <div>
                <Title level={4}>Appearance</Title>
                <Text type="secondary">Tema dan warna antarmuka.</Text>
              </div>
            </div>
            <Divider />

            <SettingItem icon={<DesktopOutlined />} title="Mode tampilan" description="Pilih tema terang atau gelap.">
              <Segmented
                value={preferences.mode}
                options={[{ label: 'Light', value: 'light' }, { label: 'Dark', value: 'dark' }]}
                onChange={(value) => updatePreference('mode', value)}
              />
            </SettingItem>

            <Divider />
            <SettingItem icon={<BgColorsOutlined />} title="Warna utama" description="Digunakan pada tombol, menu aktif, dan aksen aplikasi.">
              <div className="settings-colors" role="radiogroup" aria-label="Warna tema">
                {colorPresets.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`settings-color ${preferences.primaryColor === color.value ? 'is-active' : ''}`}
                    style={{ '--theme-swatch': color.value }}
                    title={color.name}
                    aria-label={color.name}
                    onClick={() => updatePreference('primaryColor', color.value)}
                  />
                ))}
              </div>
            </SettingItem>
          </Card>

          <Card className="settings-card" bordered={false}>
            <div className="settings-card__heading">
              <LayoutOutlined />
              <div>
                <Title level={4}>Layout & Experience</Title>
                <Text type="secondary">Sesuaikan kepadatan dan perilaku antarmuka.</Text>
              </div>
            </div>
            <Divider />

            <SettingItem icon={<CompressOutlined />} title="Compact mode" description="Tampilkan komponen lebih rapat agar lebih banyak data muat di layar.">
              <Switch checked={preferences.compact} onChange={(value) => updatePreference('compact', value)} />
            </SettingItem>
            <Divider />
            <SettingItem icon={<ThunderboltOutlined />} title="Animasi antarmuka" description="Aktifkan transisi halus pada komponen aplikasi.">
              <Switch checked={preferences.animations} onChange={(value) => updatePreference('animations', value)} />
            </SettingItem>
            <Divider />
            <SettingItem icon={<LayoutOutlined />} title="Sticky header" description="Pertahankan header di atas saat halaman digulir.">
              <Switch checked={preferences.stickyHeader} onChange={(value) => updatePreference('stickyHeader', value)} />
            </SettingItem>
            <Divider />
            <SettingItem icon={<ExpandOutlined />} title="Lebar konten" description="Gunakan lebar penuh atau area kerja yang lebih fokus.">
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={preferences.contentWidth}
                options={[{ label: 'Full', value: 'fluid' }, { label: 'Focused', value: 'focused' }]}
                onChange={(e) => updatePreference('contentWidth', e.target.value)}
              />
            </SettingItem>
          </Card>
        </Col>

        <Col xs={24} xl={9}>
          <Card className="settings-preview-card" bordered={false}>
            <Text type="secondary">LIVE PREVIEW</Text>
            <Title level={3}>Your workspace</Title>
            <Text type="secondary">Perubahan diterapkan langsung tanpa perlu logout.</Text>
            <div className="settings-preview-window">
              <div className="settings-preview-window__bar"><span /><span /><span /></div>
              <div className="settings-preview-window__body">
                <div className="settings-preview-window__sidebar" />
                <div className="settings-preview-window__content">
                  <div className="settings-preview-window__line is-title" />
                  <div className="settings-preview-window__line" />
                  <div className="settings-preview-window__line is-short" />
                  <div className="settings-preview-window__button" />
                </div>
              </div>
            </div>
            <Divider />
            <Space direction="vertical" size={2}>
              <Text strong>Tip</Text>
              <Text type="secondary">Gunakan compact mode untuk tabel payroll, attendance, KPI, dan data karyawan yang padat.</Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Settings;
